import { useState } from 'react';
import {
  Plus,
  MessageSquare,
  Trash2,
  Loader2,
  MessageCircle,
  FileText,
  Search,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { ResearchThread } from '@/reactQuery/researchAssistant';

interface ThreadListProps {
  threads: ResearchThread[];
  activeThreadId: string | null;
  isLoading: boolean;
  onSelectThread: (threadId: string) => void;
  onNewThread: () => void;
  onDeleteThread: (threadId: string) => void;
  /** Set true while createThread mutation is pending */
  isCreating?: boolean;
  /** Thread ID currently being deleted (for spinner on that row) */
  deletingThreadId?: string | null;
  /** ISO timestamp of when the analysis was last completed. Threads
   *  last active before this date are marked "stale". */
  analyzedAt?: string;
}

/**
 * Localised relative-date formatter for thread list rows.
 *
 * Uses the dashboard translation system for the "Today / Yesterday /
 * {n}d ago" labels and the browser's own locale for the longer dd-mmm
 * fallback. Passing the lang object lets us call this from inside the
 * component without making it a hook itself.
 */
function formatDate(dateStr: string, lang: { get: (key: string) => string }): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return lang.get('msg.today');
  if (diffDays === 1) return lang.get('msg.yesterday');
  if (diffDays < 7) {
    return lang.get('msg.daysAgoShort').replace('{count}', String(diffDays));
  }
  // Honour the browser locale instead of hardcoding en-GB so dd-mmm
  // formatting matches the user's regional preference.
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * "How it works" tutorial shown when the thread list is empty.
 * Helps first-time users understand the feature instead of staring at
 * a blank "No threads yet" message.
 */
function HowItWorksTutorial() {
  const { lang } = useTranslation();
  const steps = [
    {
      icon: MessageCircle,
      color: 'bg-primary/10 text-primary',
      title: lang.get('msg.raAskAnything'),
      body: lang.get('msg.raAskAnythingBody'),
    },
    {
      icon: Search,
      color: 'bg-primary/10 text-primary',
      title: lang.get('msg.raAiExplores'),
      body: lang.get('msg.raAiExploresBody'),
    },
    {
      icon: FileText,
      color: 'bg-primary/10 text-primary',
      title: lang.get('msg.raGetInsights'),
      body: lang.get('msg.raGetInsightsBody'),
    },
  ];

  return (
    <div className="px-3 py-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-3 px-1">
        {lang.get('msg.raHowItWorks')}
      </p>
      <div className="flex flex-col gap-3">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={idx} className="flex items-start gap-2.5">
              <div
                className={cn(
                  'flex items-center justify-center h-7 w-7 rounded-lg shrink-0',
                  step.color,
                )}
              >
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-neutral-700 leading-tight">{step.title}</p>
                <p className="text-[11px] text-neutral-500 leading-snug mt-0.5">{step.body}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Skeleton placeholder for thread rows while the list is loading. */
function ThreadListSkeleton() {
  return (
    <div className="px-3 py-3 flex flex-col gap-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-start gap-2 animate-pulse">
          <div className="h-3.5 w-3.5 bg-neutral-200 rounded mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="h-3 bg-neutral-200 rounded w-3/4 mb-1.5" />
            <div className="h-2 bg-neutral-100 rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ThreadList({
  threads,
  activeThreadId,
  isLoading,
  onSelectThread,
  onNewThread,
  onDeleteThread,
  isCreating,
  deletingThreadId,
  analyzedAt,
}: ThreadListProps) {
  const { lang } = useTranslation();
  // Sprint A audit fix: replace window.confirm with Radix AlertDialog.
  // window.confirm is blocked in iframes (which client portals often
  // use to embed Tellet), is unstyleable, and disrupts the UX. The
  // dialog state tracks the thread being confirmed; null means the
  // dialog is closed.
  const [confirmDeleteThread, setConfirmDeleteThread] = useState<ResearchThread | null>(null);

  return (
    <div className="flex flex-col h-full border-r border-neutral-200 bg-neutral-50/50">
      <AlertDialog
        open={confirmDeleteThread !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteThread(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{lang.get('msg.raDeleteThread')}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDeleteThread
                ? `"${confirmDeleteThread.title}" ${lang.get('msg.raDeleteThreadConfirm')}`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{lang.get('msg.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDeleteThread) {
                  onDeleteThread(confirmDeleteThread._id);
                  setConfirmDeleteThread(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {lang.get('msg.raDeleteThreadButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Thread button — sticky header. Fixed h-14 (56px) so the
          three Research Assistant columns (threads, chat, artifacts)
          have aligned header bars. The Button itself is h-9; the
          enclosing flex container centres it vertically inside the
          56px bar. */}
      <div className="h-14 px-3 flex items-center justify-center border-b border-neutral-200 bg-white/50 shrink-0">
        <Button
          variant="default"
          size="sm"
          className="w-full h-9 gap-1.5 shadow-sm self-center"
          onClick={onNewThread}
          disabled={isCreating}
          aria-label="Create new thread"
        >
          {isCreating ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {lang.get('msg.raCreating')}
            </>
          ) : (
            <>
              <Plus className="h-3.5 w-3.5" />
              {lang.get('msg.raNewThread')}
            </>
          )}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && <ThreadListSkeleton />}

        {!isLoading && threads.length === 0 && <HowItWorksTutorial />}

        {threads.map((thread) => {
          const isActive = activeThreadId === thread._id;
          const isDeleting = deletingThreadId === thread._id;
          const isStale = !!(
            analyzedAt &&
            thread.updatedAt &&
            new Date(thread.updatedAt) < new Date(analyzedAt)
          );

          return (
            // Use a div with role="button" to avoid invalid nested <button> HTML.
            // Keyboard support: Enter/Space activates select, matching native button.
            <div
              key={thread._id}
              role="button"
              tabIndex={0}
              onClick={() => !isDeleting && onSelectThread(thread._id)}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !isDeleting) {
                  e.preventDefault();
                  onSelectThread(thread._id);
                }
              }}
              className={cn(
                'w-full text-left px-3 py-2.5 border-b border-neutral-100 hover:bg-neutral-100 transition-colors group cursor-pointer focus:outline-none focus:bg-neutral-100',
                isActive && 'bg-white border-l-4 border-l-primary hover:bg-white',
                isDeleting && 'opacity-50 pointer-events-none',
              )}
            >
              <div className="flex items-start gap-2">
                <MessageSquare
                  className={cn(
                    'h-3.5 w-3.5 mt-0.5 shrink-0',
                    isActive ? 'text-primary' : 'text-neutral-400',
                  )}
                />
                <div className="flex-1 min-w-0">
                  {/* title attribute surfaces the full thread name on
                      hover/focus when the line is truncated. */}
                  <p
                    title={thread.title}
                    className={cn(
                      'text-sm font-medium truncate',
                      isActive ? 'text-neutral-900' : 'text-neutral-700',
                    )}
                  >
                    {thread.title}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-neutral-400">
                      {formatDate(thread.updatedAt, lang)}
                    </span>
                    {thread.message_count > 0 && (
                      <span className="text-[10px] text-neutral-300">
                        {thread.message_count} {thread.message_count === 1 ? lang.get('msg.raMsg') : lang.get('msg.raMsgs')}
                      </span>
                    )}
                    {isStale && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[9px] font-medium text-amber-600"
                        title={lang.get('msg.threadStaleTooltip')}
                      >
                        <AlertTriangle className="h-2.5 w-2.5" />
                        {lang.get('msg.stale')}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isDeleting) return;
                    setConfirmDeleteThread(thread);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  disabled={isDeleting}
                  className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 p-2 hover:text-red-500 focus:text-red-500 text-neutral-500 transition-opacity focus:outline-none focus:ring-2 focus:ring-red-200 rounded disabled:opacity-100 min-h-[28px] min-w-[28px] flex items-center justify-center"
                  aria-label={`Delete thread ${thread.title}`}
                >
                  {isDeleting ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer with thread count when threads exist */}
      {!isLoading && threads.length > 0 && (
        <div className="px-3 py-2 border-t border-neutral-200 bg-white/30">
          <p className="text-[10px] text-neutral-400">
            {threads.length} {threads.length === 1 ? lang.get('msg.raThread') : lang.get('msg.raThreads')}
          </p>
        </div>
      )}
    </div>
  );
}

export default ThreadList;
